import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  MAX_EVIDENCE_BYTES,
  assignmentResponseSchema,
  conditionSchema,
  createTransferResponseSchema,
  rotationInResponseSchema,
  rotationReasonSchema,
  transferRecipientsResponseSchema,
  type Assignment,
  type Condition,
  type RotationReason,
  type TransferRecipient,
} from '@unit/shared';
import { apiGet, apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { PersonName } from '../../components/PersonName.js';
import { SerialPicker } from '../issuance/LineEditor.js';

/**
 * What can happen to one assignment after it was signed (REQUIREMENTS §2.5):
 * return, expend, report lost/damaged, found, write-off.
 *
 * `canManage` is the viewer's *role* — a commander or the storeroom looking at
 * a soldier — and is a convenience only: every button calls an endpoint that
 * re-checks whether this caller reaches this soldier. `isOwner` opens the one
 * action a soldier takes on their own gear: reporting it lost or damaged.
 */
export type GearAction = 'return' | 'expend' | 'report' | 'found' | 'writeOff' | 'rotateOut' | 'rotateIn' | 'transfer';

export function availableActions(a: Assignment, opts: { isOwner: boolean; canManage: boolean; storeroom: boolean }): GearAction[] {
  const open = a.status === 'active' || a.status === 'in_rotation';
  // Platoon store (story 30): a distributed item goes back to the sergeant
  // and a holding with gear out stays put — no transfer, no rotation.
  const store = a.sourceAssignmentId !== null || a.distributedQuantity > 0;
  const out: GearAction[] = [];
  if (open && opts.canManage) out.push(a.consumable ? 'expend' : 'return');
  // Rotation (§2.5), either order: hand the old one in, issue the new one.
  if (a.status === 'active' && !a.consumable && !store && opts.canManage) out.push('rotateOut');
  if (open && !a.consumable && !store && opts.canManage) out.push('rotateIn');
  // Transfer (§3.2): the holder hands it on, or the chain / storeroom does on their behalf.
  if (a.status === 'active' && !a.transferId && a.transferable && !store && (opts.isOwner || opts.canManage)) out.push('transfer');
  if (open && !a.consumable && (opts.isOwner || opts.canManage)) out.push('report');
  if ((a.status === 'lost' || a.status === 'damaged') && opts.canManage) out.push('found');
  if ((open || a.status === 'lost' || a.status === 'damaged') && opts.storeroom) out.push('writeOff');
  return out;
}

const input = 'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const danger = 'rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40';

export function GearActionPanel({
  assignment: a,
  action,
  onDone,
  onClose,
}: {
  assignment: Assignment;
  action: GearAction;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid={`gear-panel-${action}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-slate-600">{t(`gear.${action}Hint`)}</p>
        <button type="button" onClick={onClose} className="text-xs text-slate-500 underline-offset-4 hover:underline">
          {t('gear.close')}
        </button>
      </div>
      {action === 'return' && <ReturnForm a={a} onDone={onDone} />}
      {action === 'expend' && <ExpendForm a={a} onDone={onDone} />}
      {action === 'report' && <ReportForm a={a} onDone={onDone} />}
      {action === 'found' && <FoundForm a={a} onDone={onDone} />}
      {action === 'writeOff' && <WriteOffForm a={a} onDone={onDone} />}
      {action === 'rotateOut' && <RotateOutForm a={a} onDone={onDone} />}
      {action === 'rotateIn' && <RotateInForm a={a} />}
      {action === 'transfer' && <TransferForm a={a} />}
    </div>
  );
}

/**
 * The receiver is found by name or personal number (owner, 2026-09-21) —
 * the server searches, and answers with names and placements only, so a
 * soldier still reads nobody else's file (§3.7). On success this goes to
 * the receiver's pending form.
 */
function TransferForm({ a }: { a: Assignment }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [receiver, setReceiver] = useState<TransferRecipient | null>(null);
  const [reason, setReason] = useState('');
  const act = useAction(() =>
    apiPost('/transfers', createTransferResponseSchema, {
      assignmentId: a.id,
      toUid: receiver?.uid ?? '',
      reason: reason.trim() || null,
    }),
  );
  return (
    <>
      <div className="mt-2 text-sm text-slate-600">
        {t('gear.transferTo')}
        {receiver ? (
          <p className="mt-1 flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-1.5 ring-1 ring-slate-300" data-testid="transfer-receiver">
            <span className="text-slate-900">
              <PersonName person={receiver} />
            </span>
            {receiver.placementName && <span className="text-xs text-slate-500">{receiver.placementName}</span>}
            <button type="button" onClick={() => setReceiver(null)} className="ms-auto text-xs text-slate-500 underline-offset-4 hover:underline" data-testid="transfer-receiver-change">
              {t('gear.transferChange')}
            </button>
          </p>
        ) : (
          <RecipientSearch onPick={setReceiver} />
        )}
      </div>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.notes')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-reason" />
      </label>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && navigate(`/forms/${r.form.id}`))}
        disabled={act.busy || !receiver}
        className={`mt-3 ${primary}`}
        data-testid="transfer-submit"
      >
        {t('gear.confirmTransfer')}
      </button>
      <div className="mt-2">
        <ActionError error={act.error} />
        {act.error?.code === 'not_found' && <p className="text-sm text-slate-600">{t('gear.transferNoSuchSoldier')}</p>}
        {act.error?.details.reason === 'same_holder' && <p className="text-sm text-slate-600">{t('gear.transferSameHolder')}</p>}
      </div>
    </>
  );
}

/** A searchable text input: two characters on, the server answers with up to ten people. */
function RecipientSearch({ onPick }: { onPick: (r: TransferRecipient) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'searching' } | { kind: 'ready'; rows: TransferRecipient[] }>({ kind: 'idle' });

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'searching' });
    const handle = setTimeout(() => {
      apiGet(`/transfers/recipients?q=${encodeURIComponent(query)}`, transferRecipientsResponseSchema)
        .then((r) => !cancelled && setState({ kind: 'ready', rows: r.recipients }))
        .catch(() => !cancelled && setState({ kind: 'ready', rows: [] }));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q]);

  return (
    <div>
      <input
        className={input}
        placeholder={t('gear.transferSearch')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
        data-testid="transfer-search"
      />
      {state.kind === 'searching' && <p className="mt-1 text-xs text-slate-500">{t('gear.transferSearching')}</p>}
      {state.kind === 'ready' && state.rows.length === 0 && (
        <p className="mt-1 text-xs text-slate-500" data-testid="transfer-no-matches">{t('gear.transferNoMatches')}</p>
      )}
      {state.kind === 'ready' && state.rows.length > 0 && (
        <ul className="mt-1 divide-y divide-slate-100 rounded-lg bg-white ring-1 ring-slate-200" data-testid="transfer-matches">
          {state.rows.map((r) => (
            <li key={r.uid}>
              <button
                type="button"
                onClick={() => onPick(r)}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-1.5 text-start text-sm hover:bg-slate-50"
                data-testid="transfer-match"
                data-uid={r.uid}
              >
                <span className="text-slate-900">
                  <PersonName person={r} />
                </span>
                {r.placementName && <span className="text-xs text-slate-500">{r.placementName}</span>}
                {r.personalNumber && <span className="latin-inline ms-auto text-xs text-slate-500">{r.personalNumber}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RotateOutForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<RotationReason>('cleaning');
  const [notes, setNotes] = useState('');
  const act = useAction(() =>
    apiPost(`/assignments/${a.id}/rotation-out`, assignmentResponseSchema, { reason, notes: notes.trim() || null }),
  );
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.rotationReason')}
        <select className={input} value={reason} onChange={(e) => setReason(rotationReasonSchema.parse(e.target.value))} data-testid="rotate-out-reason">
          {rotationReasonSchema.options.map((r) => (
            <option key={r} value={r}>{t(`gear.rotationReasons.${r}`)}</option>
          ))}
        </select>
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rotate-out-notes" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy} className={`mt-3 ${primary}`} data-testid="rotate-out-submit">
        {t('gear.confirmRotateOut')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}

/**
 * The replacement is a fresh form the soldier signs, so on success this
 * navigates to it rather than staying on the card.
 */
function RotateInForm({ a }: { a: Assignment }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serialized = a.serialNumber !== null;
  const [unit, setUnit] = useState<{ id: string; serialNumber: string } | null>(null);
  const [quantity, setQuantity] = useState(a.quantity);
  const [condition, setCondition] = useState<Condition>('good');
  const act = useAction(() =>
    apiPost(`/assignments/${a.id}/rotation-in`, rotationInResponseSchema, {
      replacement: { inventoryUnitId: unit?.id ?? null, quantity: serialized ? 1 : quantity, condition, notes: null },
    }),
  );
  return (
    <>
      <div className="mt-2">
        {serialized ? (
          <SerialPicker catalogItemId={a.catalogItemId} selected={unit} onSelect={setUnit} />
        ) : (
          <label className="block text-sm text-slate-600">
            {t('gear.quantity')}
            <input type="number" min={1} className={`${input} latin-inline`} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} data-testid="rotate-in-quantity" />
          </label>
        )}
      </div>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.condition')}
        <select className={input} value={condition} onChange={(e) => setCondition(conditionSchema.parse(e.target.value))} data-testid="rotate-in-condition">
          {conditionSchema.options.map((c) => (
            <option key={c} value={c}>{t(`inventory.condition.${c}`)}</option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && navigate(`/forms/${r.form.id}`))}
        disabled={act.busy || (serialized ? !unit : !Number.isInteger(quantity) || quantity < 1)}
        className={`mt-3 ${primary}`}
        data-testid="rotate-in-submit"
      >
        {t('gear.confirmRotateIn')}
      </button>
      <div className="mt-2">
        <ActionError error={act.error} />
        {act.error?.details.reason === 'replacement_exists' && (
          <p className="text-sm text-slate-600">{t('gear.replacementExists')}</p>
        )}
      </div>
    </>
  );
}

export function ReturnForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  const [condition, setCondition] = useState<Condition>('good');
  const [notes, setNotes] = useState('');
  const act = useAction(() =>
    apiPost(`/assignments/${a.id}/return`, assignmentResponseSchema, { condition, notes: notes.trim() || null }),
  );
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.condition')}
        <select className={input} value={condition} onChange={(e) => setCondition(conditionSchema.parse(e.target.value))} data-testid="return-condition">
          {conditionSchema.options.map((c) => (
            <option key={c} value={c}>{t(`inventory.condition.${c}`)}</option>
          ))}
        </select>
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="return-notes" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy} className={`mt-3 ${primary}`} data-testid="return-submit">
        {t('gear.confirmReturn')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}

function ExpendForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  // A sergeant's holding: what is out with soldiers is theirs to expend (story 30).
  const left = a.quantity - a.expendedQuantity - a.distributedQuantity;
  const [quantity, setQuantity] = useState(left);
  const [notes, setNotes] = useState('');
  const act = useAction(() =>
    apiPost(`/assignments/${a.id}/expend`, assignmentResponseSchema, { quantity, notes: notes.trim() || null }),
  );
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.quantity')} <span className="text-slate-400">({t('gear.remaining', { count: left, total: a.quantity })})</span>
        <input type="number" min={1} max={left} className={`${input} latin-inline`} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} data-testid="expend-quantity" />
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="expend-notes" />
      </label>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && onDone())}
        disabled={act.busy || !Number.isInteger(quantity) || quantity < 1 || quantity > left}
        className={`mt-3 ${primary}`}
        data-testid="expend-submit"
      >
        {t('gear.confirmExpend')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}

function ReportForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'lost' | 'damaged'>('lost');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const act = useAction(() =>
    apiPost(`/assignments/${a.id}/report`, assignmentResponseSchema, { kind, notes: notes.trim(), photoDataUrl: photo }),
  );

  const onFile = (file: File | undefined) => {
    setPhoto(null);
    setPhotoError(null);
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setPhotoError(t('gear.photoWrongType'));
      return;
    }
    if (file.size > MAX_EVIDENCE_BYTES) {
      setPhotoError(t('gear.photoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  };

  return (
    <>
      <fieldset className="mt-2 flex gap-4 text-sm text-slate-700">
        <legend className="text-slate-600">{t('gear.reportKind')}</legend>
        {(['lost', 'damaged'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1">
            <input type="radio" name={`kind-${a.id}`} checked={kind === k} onChange={() => setKind(k)} data-testid={`report-kind-${k}`} />
            {t(`gear.${k}`)}
          </label>
        ))}
      </fieldset>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.reportNotes')}
        <textarea className={input} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="report-notes" />
      </label>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.photo')}
        <input type="file" accept="image/jpeg,image/png" className="mt-1 block text-sm" onChange={(e) => onFile(e.target.files?.[0])} data-testid="report-photo" />
      </label>
      {photo && <p className="mt-1 text-xs text-emerald-700" data-testid="report-photo-attached">{t('gear.photoAttached')}</p>}
      {photoError && <p className="mt-1 text-xs text-red-600" role="alert">{photoError}</p>}
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && onDone())}
        disabled={act.busy || notes.trim().length < 3 || photoError !== null}
        className={`mt-3 ${danger}`}
        data-testid="report-submit"
      >
        {t('gear.submitReport')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}

function FoundForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const act = useAction(() => apiPost(`/assignments/${a.id}/found`, assignmentResponseSchema, { notes: notes.trim() || null }));
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.notes')}
        <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="found-notes" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy} className={`mt-3 ${primary}`} data-testid="found-submit">
        {t('gear.confirmFound')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}

function WriteOffForm({ a, onDone }: { a: Assignment; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const act = useAction(() => apiPost(`/assignments/${a.id}/write-off`, assignmentResponseSchema, { reason: reason.trim() }));
  return (
    <>
      <label className="mt-2 block text-sm text-slate-600">
        {t('gear.writeOffReason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="write-off-reason" />
      </label>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && onDone())}
        disabled={act.busy || reason.trim().length < 3}
        className={`mt-3 ${danger}`}
        data-testid="write-off-submit"
      >
        {t('gear.confirmWriteOff')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </>
  );
}
